// Awakening on the client (shared/awakening.ts): the creature page's Awaken card and its confirm, and the Bestiary's
// Awakened-form section. Minimal text.
import { SPECIES } from '../shared/species.ts';
import { pvpTag } from './pvp-tag.ts';
import { ITEM } from '../shared/items.ts';
import { MOVE, awakenedMoveId, AWAKENED_CD_MS } from '../shared/moves.ts';
import { STAT_NAMES } from '../shared/rolls.ts';
import { huntPower } from '../shared/power.ts';
import { awakenCheck, stoneFor } from '../shared/awakening.ts';
import type { ClientMsg, CreatureDTO } from '../shared/protocol.ts';
import { S } from './state.ts';
import { awakenedPic, iconUrl } from './assets.ts';
import { esc, full, elChip, cname } from './fmt.ts';
import { openDialog } from './dialog.ts';

const total = (b: readonly number[]) => b.reduce((a, x) => a + x, 0);

/** "Fimbulwinter Fang · Frost · 600 · 30 s" for an Awakened species. */
function moveLine(awakened: string): string {
  const m = MOVE[awakenedMoveId(awakened)];
  return `<b>${esc(m.name)}</b> ${elChip(m.element)} <span class="muted">${m.power} · ${AWAKENED_CD_MS / 1000} s</span>`;
}

/** The creature page's Awakening card: only on a species with an Awakened form, or on an Awakened creature. */
export function awakenCard(c: CreatureDTO): string {
  const sp = SPECIES[c.species];
  if (sp.awakened) {
    return `<section class="panel cp-card awk-card"><div class="cp-h"><h5>Awakened</h5>${pvpTag('full')}<span class="pill awk-pill">Final form</span></div>
      <p>${moveLine(c.species)}</p></section>`;
  }
  if (!sp.awakensTo) return '';
  const to = SPECIES[sp.awakensTo], t = S.trainer!;
  const stone = stoneFor(c), have = t.bag[stone] ?? 0;
  const chk = awakenCheck(c, t.bag);
  const after = huntPower({ ...c, species: to.id }), now = huntPower(c);
  return `<section class="panel cp-card awk-card"><div class="cp-h"><img class="cp-ico" src="${iconUrl(stone)}" alt=""><h5>Awaken to ${esc(to.name)}</h5>${pvpTag('full')}${chk.ok ? '<span class="pill good">Ready</span>' : ''}</div>
    <p class="cp-prev">${to.elements.map(elChip).join('')} · Base ${total(sp.base)} → <b>${total(to.base)}</b> · Power ${full(now)} → <b>${full(after)}</b></p>
    <p>${moveLine(to.id)}</p>
    <ul class="cp-check"><li class="${have ? 'ok' : 'no'}">${have ? '✓' : '✗'} 1 ${esc(ITEM[stone].name)} (you have ${full(have)})</li></ul>
    <button class="btn gold" data-act="awaken" data-id="${c.id}"${chk.ok ? '' : ` disabled title="${esc(chk.why)}"`}>Awaken…</button></section>`;
}

/** Confirm, then send. Irreversible. */
export function awakenConfirm(id: number, send: (m: ClientMsg) => void) {
  const c = S.creatures.get(id);
  if (!c) return;
  const to = SPECIES[SPECIES[c.species].awakensTo ?? ''];
  if (!to) return;
  const stone = ITEM[stoneFor(c)];
  void openDialog({
    title: `Awaken ${cname(c)}?`,
    body: `<p>It becomes ${esc(to.name)} for good, using 1 ${esc(stone.name)}. It can't be undone.</p>`,
    danger: true,
    actions: [{ label: 'Awaken', value: 'awaken', kind: 'gold' }],
  }).then((r) => { if (r.value) send({ t: 'awaken', id }); });
}

/** Bestiary: the Awakened form of a species (or the base of an Awakened one), stats side by side. */
export function awakenSection(id: string): string {
  const sp = SPECIES[id];
  const baseId = sp.awakenedFrom ?? (sp.awakensTo ? id : '');
  if (!baseId) return '';
  const b = SPECIES[baseId], a = SPECIES[b.awakensTo!];
  const row = (n: string, i: number) => `<tr><td>${n}</td><td class="num">${b.base[i]}</td><td class="num"><b>${a.base[i]}</b></td></tr>`;
  return `<h4>Awakening</h4>
    <div class="sp-chain"><div class="sp-node${id === baseId ? ' on' : ''}" data-act="dex-open" data-id="${baseId}">${awakenedPic(baseId)}<span>${esc(b.name)}</span></div>
      <span class="arrow">→<small>${esc(ITEM[`awakening-stone-${baseId}`]?.name ?? 'Awakening Stone')}</small></span>
      <div class="sp-node${id === a.id ? ' on' : ''}" data-act="dex-open" data-id="${a.id}">${awakenedPic(a.id)}<span>${esc(a.name)}</span></div></div>
    <table class="cp-stats awk-stats"><thead><tr><th></th><th class="num">${esc(b.name)}</th><th class="num">${esc(a.name)}</th></tr></thead><tbody>
      <tr><td>Elements</td><td class="num">${b.elements.map(elChip).join('')}</td><td class="num">${a.elements.map(elChip).join('')}</td></tr>
      ${STAT_NAMES.map(row).join('')}
      <tr><td>Total</td><td class="num">${total(b.base)}</td><td class="num"><b>${total(a.base)}</b></td></tr></tbody></table>
    <p class="sub">${moveLine(a.id)}</p>`;
}
